// @flow

import * as React from "react";
import { withRouter } from "react-router-dom";
import { format } from "date-fns";

import InquiryPage from "../../components/InquiryPage";
import DataFetcher from "../../components/DataFetcher";
import { FlashMessageConsumer } from "../../components/FlashMessages";
import * as formStyles from "../../utils/formStyles";
import { denormalizeJsonApi } from "../../utils/denormalizeJsonApi";
import { generateInitialValues } from "../../utils/helpers";
import PersonalInfo from "./PersonalInfo";

import {
  COMPANY__ROLE,
  FEDERAL_STATE,
  BANK_CODE,
  BANK_NAME,
  BANK_ADVISOR,
  COMPANY__NAME,
  COMPANY__STREET,
  COMPANY__FOUNDING_DATE,
  COMPANY__LOCATION,
  COMPANY__LEGAL_FORM,
  AMOUNT,
  PROJECT_START,
  COMPANY__CONTACT_PERSON_NAME,
  COMPANY__CONTACT_PERSON_GENDER,
  COMPANY__CONTACT_PERSON_PRIVACY_POLICY,
  COMPANY__CONTACT_PERSON_EMAIL,
  COMPANY__CONTACT_PERSON_PHONE_NUMBER,
  COMPANY__CONTACT_PERSON_COMMUNICATION_ONLY_VIA_BANK_ADVISOR,
  INFORMATION_ON_GRANTS,
  PURPOSE_KIND,
  FINANCING_PRODUCT,
  FINANCING_DESCRIPTION,
  FINANCING_AMOUNT,
  INVESTMENT_AMOUNT,
  IS_ALREADY_SELF_EMPLOYED,
  IS_BANKRUPT,
  IS_SME,
  HAS_SHARES_IN_OTHER_COMPANY,
  COLLATERALS,
  REMARK,
  UWD_CHAMBER,
  UWD_CHAMBER_EXPERT,
  UWD_CHAMBER_PROJECT
} from "./formFields";

import OkpCompanyInfo from "./OkpCompanyInfo";
import OkpProjectInfo from "./OkpProjectInfo";
import OkpContactPersonInfo from "./OkpContactPersonInfo";
import OkpGrantsInfo from "./OkpGrantsInfo";
import OkpFinancingInfo from "./OkpFinancingInfo";
import OkpAffirmationInfo from "./OkpAffirmationInfo";
import OkpCollateralInfo from "./OkpCollateralInfo";
import OkpRemarksInfo from "./OkpRemarksInfo";
import UwdInfo from "./UwdInfo";

export type BankActions = {
  getBanksForDetails: ({ id: number }) => Promise<*>,
  getBanks: (text: string) => Promise<*>,
  createExcludedBank: ({ id: number, bankId: number }) => Promise<*>,
  deleteExcludedBank: ({ id: number, bankId: number }) => Promise<*>,
  updateBankSuggestionContactPerson: ({
    id: number,
    bankId: number,
    first_name: string,
    last_name: string
  }) => Promise<*>,
  updateUsedBanksContactPerson: ({
    id: number,
    bankId: number,
    first_name: string,
    last_name: string
  }) => Promise<*>,
  updateInquiry: ({ id: number, details: any }) => Promise<*>
};

type DetailsProps = {
  match: {
    params: {
      id: number
    }
  },
  getTabs: (
    inquiryType: string,
    financingProcess: any
  ) => (
    inquiryId: number,
    financingProcess: any
  ) => Array<{
    path: string,
    label: string,
    icon: string
  }>,
  backPath: boolean => string,
  getInquiry: ({ id: number }) => Promise<*>,
  site: "buba" | "bank" | "customer",
  renderPrepended: ({ data: any, requestFetch: () => void }) => React.Node,
  showStatus?: boolean,
  bankActions: BankActions,
  generatePDFtemplate?: (inquiryType: string) => (...args: any) => any,
  downloadPdf: () => void
};

const DetailsNew = ({
  match: {
    params: { id }
  },
  getTabs,
  backPath,
  getInquiry,
  site,
  renderPrepended = () => null,
  showStatus,
  generatePDFtemplate
}: DetailsProps) => {
  const [isBankDataLoading] = React.useState(false);
  const [bankData] = React.useState(null);

  return (
    <FlashMessageConsumer>
      {() => (
        <DataFetcher
          apiAction={getInquiry}
          mapper={denormalizeJsonApi}
          initialPayload={{ id: Number(id) }}
          redirectOnError
        >
          {res => {
            const { data, isLoading, requestFetch } = res;
            if (isLoading) return null;
            const { bank, creator, company, financingRequest, createdAt } = data;
            const initialInquiryDetails = {
              ...generateInitialValues(financingRequest.details),
              [FEDERAL_STATE]: financingRequest.details.federalState
                ? financingRequest.details.federalState
                : "",
              [AMOUNT]: financingRequest.projectAmount
            };

            const initialCompanyInfo = generateInitialValues(company);

            const inquiryType = creator.type;

            return (
              <div className={formStyles.FIELD_STYLE_SUMMARY}>
                <InquiryPage
                  companyName={company.name}
                  issuedOn={new Date(createdAt)}
                  isLoadingData={isLoading || isBankDataLoading}
                  backPath={backPath(inquiryType)}
                  tabs={getTabs(inquiryType, data)}
                  site={site}
                  {...(showStatus ? { status: financingRequest.status } : {})}
                  generatePDFtemplate={generatePDFtemplate && generatePDFtemplate(inquiryType)}
                  pdfData={{ ...data, bankData }}
                >
                  {renderPrepended({ data, requestFetch })}

                  <UwdInfo
                    initialValues={{
                      [UWD_CHAMBER]: initialInquiryDetails.uwd?.chamber,
                      [UWD_CHAMBER_EXPERT]: initialInquiryDetails.uwd?.chamberExpert,
                      [UWD_CHAMBER_PROJECT]: initialInquiryDetails.uwd?.chamberProject
                    }}
                  />

                  <>
                    {site === "buba" && (
                      <PersonalInfo
                        numbered={false}
                        initialValues={{
                          ...generateInitialValues(creator),
                          [BANK_CODE]: bank.code,
                          [BANK_NAME]: bank.name,
                          [BANK_ADVISOR]: `${creator.firstName} ${creator.lastName}`
                        }}
                      />
                    )}

                    <OkpCompanyInfo
                      initialValues={{
                        [COMPANY__NAME]: initialCompanyInfo.name,
                        [COMPANY__STREET]: initialCompanyInfo.street,
                        [COMPANY__FOUNDING_DATE]: format(
                          initialCompanyInfo.founding_date,
                          "dd.MM.yyyy"
                        ),
                        [COMPANY__LOCATION]: `${initialCompanyInfo.zip_code} ${initialCompanyInfo.city}`,
                        [COMPANY__LEGAL_FORM]: initialCompanyInfo.legal_form
                      }}
                      numberedWithDot
                    />

                    <OkpProjectInfo
                      initialValues={{
                        [AMOUNT]: initialInquiryDetails.amount,
                        [COMPANY__ROLE]: initialInquiryDetails.company_role,
                        [PROJECT_START]: initialInquiryDetails.project_start,
                        [FEDERAL_STATE]: initialInquiryDetails.federal_state
                      }}
                      numberedWithDot
                    />

                    <OkpContactPersonInfo
                      initialValues={{
                        [COMPANY__CONTACT_PERSON_NAME]: initialCompanyInfo.contact_person
                          ? `${initialCompanyInfo.contact_person.firstName} ${initialCompanyInfo.contact_person.lastName}`
                          : null,
                        [COMPANY__CONTACT_PERSON_GENDER]: initialCompanyInfo.contact_person?.gender,
                        [COMPANY__CONTACT_PERSON_PRIVACY_POLICY]:
                          initialInquiryDetails.privacy_policy_consent,
                        [COMPANY__CONTACT_PERSON_EMAIL]: initialCompanyInfo.contact_person?.email,
                        [COMPANY__CONTACT_PERSON_PHONE_NUMBER]:
                          initialCompanyInfo.contact_person?.phoneNumber,
                        [COMPANY__CONTACT_PERSON_COMMUNICATION_ONLY_VIA_BANK_ADVISOR]:
                          initialInquiryDetails?.communication_only_via_bank_advisor_consent
                      }}
                      numberedWithDot
                    />

                    <OkpFinancingInfo
                      initialValues={{
                        [PURPOSE_KIND]: initialInquiryDetails.use_of_funds?.purposeKind,
                        [FINANCING_PRODUCT]: initialInquiryDetails.source_of_funds?.productKind,
                        [FINANCING_DESCRIPTION]: initialInquiryDetails.financing_description,
                        [FINANCING_AMOUNT]:
                          initialInquiryDetails.source_of_funds?.totalFinancingAmount,
                        [INVESTMENT_AMOUNT]:
                          initialInquiryDetails.use_of_funds?.totalInvestmentAmount
                      }}
                      numberedWithDot
                    />

                    <OkpGrantsInfo
                      initialValues={{
                        [INFORMATION_ON_GRANTS]: initialInquiryDetails[INFORMATION_ON_GRANTS]
                      }}
                      numberedWithDot
                    />

                    <OkpAffirmationInfo
                      initialValues={{
                        [IS_ALREADY_SELF_EMPLOYED]: initialInquiryDetails[IS_ALREADY_SELF_EMPLOYED],
                        [IS_BANKRUPT]: initialInquiryDetails[IS_BANKRUPT],
                        [IS_SME]: initialInquiryDetails[IS_SME],
                        [HAS_SHARES_IN_OTHER_COMPANY]:
                          initialInquiryDetails[HAS_SHARES_IN_OTHER_COMPANY]
                      }}
                      numberedWithDot
                    />

                    <OkpCollateralInfo
                      initialValues={{
                        [COLLATERALS]: initialInquiryDetails[COLLATERALS]
                      }}
                      numberedWithDot
                    />

                    <OkpRemarksInfo
                      initialValues={{
                        [REMARK]: initialInquiryDetails[REMARK]
                      }}
                    />
                  </>
                </InquiryPage>
              </div>
            );
          }}
        </DataFetcher>
      )}
    </FlashMessageConsumer>
  );
};

export default withRouter(DetailsNew);
